import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';

const API_URL = '';

export default function LoginPage({ onLogin }) {
  const isLogin = true;
  useEffect(() => {
    const err = new URLSearchParams(window.location.search).get('error');
    if (err === 'no_account') toast.error('No portal account exists for that Google email. Ask your administrator to create one.');
    if (err === 'disabled') toast.error('This account is disabled. Please contact your administrator.');
  }, []);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone_number: '',
    role: 'design',  // Default employee access level - Admin can change later
    department: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let response;
      if (isLogin) {
        // Use form-encoded POST to avoid CORS preflight
        const body = new URLSearchParams();
        body.append('email', formData.email);
        body.append('password', formData.password);
        response = await fetch(`${API_URL}/api/auth/login-form`, {
          method: 'POST',
          body: body,
        });
      } else {
        response = await fetch(`${API_URL}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
      }
      const data = await response.json();
      if (!response.ok) {
        toast.error(data.detail || 'Authentication failed');
        return;
      }
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      onLogin(data.user, data.access_token);
    } catch (error) {
      toast.error('Network error. Please try again.');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email address');
      return;
    }
    
    setSendingReset(true);
    try {
      const body = new URLSearchParams();
      body.append('email', forgotEmail);
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const data = await response.json();
      if (!response.ok) {
        toast.error(data.detail || 'Failed to send reset email');
      } else {
        toast.success('Password reset link sent! Check your email.');
        setShowForgotPassword(false);
        setForgotEmail('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setSendingReset(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grid-background flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5 pointer-events-none" />
      
      <div className="relative w-full max-w-md">
        <div className="flex items-center justify-center gap-4 mb-8">
          <img 
            src="/desq-logo.png" 
            alt="Desq Logo" 
            className="w-28 h-28 object-contain"
            data-testid="company-logo"
          />
        </div>

        <Card className="glass-effect border-border/40" data-testid="login-card">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl">{isLogin ? 'Welcome Back' : 'Create Account'}</CardTitle>
            <CardDescription>
              {isLogin ? 'Sign in to access your portal' : 'Register for Desq Employee Portal'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Full Name</Label>
                    <Input
                      id="full_name"
                      data-testid="input-full-name"
                      placeholder="John Doe"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      required={!isLogin}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone_number">Phone Number</Label>
                    <Input
                      id="phone_number"
                      type="tel"
                      data-testid="input-phone-number"
                      placeholder="+63 912 345 6789"
                      value={formData.phone_number}
                      onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                      required={!isLogin}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="department">Department</Label>
                    <select
                      id="department"
                      data-testid="select-department"
                      className="flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background focus:ring-2 focus:ring-primary/20"
                      value={formData.department}
                      onChange={(e) => {
                        const dept = e.target.value;
                        // Auto-set role based on department
                        const role = dept === 'Construction' ? 'construction' : 'design';
                        setFormData({ ...formData, department: dept, role: role });
                      }}
                      required={!isLogin}
                    >
                      <option value="">Select Department</option>
                      <option value="Design">Design</option>
                      <option value="Construction">Construction</option>
                    </select>
                    <p className="text-xs text-muted-foreground">
                      Your access level will be set by Admin after registration
                    </p>
                  </div>
                </>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="input-email"
                  placeholder="you@desq.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    data-testid="input-password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="btn-toggle-password"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-xs text-primary hover:underline"
                    data-testid="btn-forgot-password"
                  >
                    Forgot Password?
                  </button>
                )}
              </div>

              <Button type="submit" className="w-full hover-lift" data-testid="btn-submit">
                {isLogin ? 'Sign In' : 'Create Account'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <p className="text-center text-xs text-muted-foreground" data-testid="login-admin-note">
                Accounts are created by your administrator.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Enter your registered email address and we'll send you a link to reset your password.
            </p>
            <div className="space-y-2">
              <Label htmlFor="forgot_email">Email Address</Label>
              <Input
                id="forgot_email"
                type="email"
                data-testid="input-forgot-email"
                placeholder="you@desq.com"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full" data-testid="btn-send-reset" disabled={sendingReset}>
              {sendingReset ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}